// skeleton/app/src/app/app.component.ts

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

import { MenubarService } from './shared/services/menunavbar.services';
import { IdleTimeoutService } from './shared/services/idle-timeout.service';
import { AuthService } from './auth/auth.service';
import { LogoutServive } from './logout/logout.service';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CommonMessageComponent } from './shared/components/common-message/common-message.component';

import { AUTH_SERVICE } from './auth/auth.token';
import { IAuthService } from './auth/auth.interface';

import { SiteminderAuthService } from './auth/oidc-config.service';
import { SilentRenewService } from './auth/silent-renew.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Health Slice';

  private errorMsgSub?: Subscription;
  private isExist = false;
  private logoutPopupRef: any;

  isAdmin: boolean = true;
  isHeaderShow: boolean = false;

  private readonly _destroyLogin = new Subject<void>();

  constructor(
    private menubarService: MenubarService,
    @Inject(AUTH_SERVICE) private authSvc: IAuthService,
    private idleTimeoutService: IdleTimeoutService,
    private router: Router,
    private authService: AuthService,
    private logoutservice: LogoutServive,
    private dialog: MatDialog,
    private siteminderAuthService: SiteminderAuthService,
    private silentRenewService: SilentRenewService
  ) {}

  resetIdleTimer(): void {
    try {
      this.idleTimeoutService.resetTimer();

      this.idleTimeoutService
        .startTimeout(60)
        .pipe(takeUntil(this._destroyLogin))
        .subscribe({
          next: () => {
            try {
              this.sessionlogOut();
            } catch (error) {
              this.logError('Idle timeout logout failed', error);
            }
          },
          error: (error) => {
            this.logError('Idle timeout failed', error);
          },
        });

      this.idleTimeoutService
        .showAlertPopupAfterIdleTime(55)
        .pipe(takeUntil(this._destroyLogin))
        .subscribe({
          next: () => {
            try {
              if (this.authSvc.isTokenValid()) {
                this.logoutViaModal();
              } else {
                this.sessionlogOut();
              }
            } catch (error) {
              this.logError('Idle warning failed', error);
              this.sessionlogOut();
            }
          },
          error: (error) => {
            this.logError('Idle warning timer failed', error);
          },
        });
    } catch (error) {
      this.logError('Idle timer reset failed', error);
    }
  }

  logoutViaModal(): void {
    try {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.width = '450px';
      dialogConfig.position = { top: '5px' };
      dialogConfig.data =
        'Session will expire in 5 minutes.\n do you want to extend the session?';

      let timeLeft = 5 * 60;

      const intervalId = setInterval(() => {
        try {
          timeLeft--;

          if (timeLeft <= 0) {
            clearInterval(intervalId);
            this.sessionlogOut();
            return;
          }

          if (this.logoutPopupRef) {
            const minutes = Math.floor(timeLeft / 60);
            const seconds = timeLeft % 60;

            this.logoutPopupRef.componentInstance.message =
              `Session will expire in ${minutes}:${seconds
                .toString()
                .padStart(2, '0')} minutes.\n do you want to extend the session?`;
          }
        } catch (error) {
          clearInterval(intervalId);
          this.logError('Session countdown failed', error);
          this.sessionlogOut();
        }
      }, 1000);

      this.logoutPopupRef = this.dialog.open(
        CommonMessageComponent,
        dialogConfig
      );

      this.logoutPopupRef
        .afterClosed()
        .pipe(takeUntil(this._destroyLogin))
        .subscribe({
          next: (res: any) => {
            clearInterval(intervalId);

            if (!res) {
              this.sessionlogOut();
            }
          },
          error: (error) => {
            clearInterval(intervalId);
            this.logError('Session dialog failed', error);
            this.sessionlogOut();
          },
        });
    } catch (error) {
      this.logError('Session dialog open failed', error);
      this.sessionlogOut();
    }
  }

  private sessionlogOut(): void {
    try {
      if (this.logoutPopupRef) {
        try {
          this.logoutPopupRef.close();
        } catch (error) {
          this.logError('Session dialog close failed', error);
        }

        this.logoutPopupRef = null;
      }

      try {
        this.menubarService.updatedDataSelection(false);
      } catch (error) {
        this.logError('Menu reset failed', error);
      }

      try {
        this.logoutservice.changeMessage(false);

        this.logoutservice.setUserLoginStatus(false);
      } catch (error) {
        this.logError('Login status reset failed', error);
      }

      try {
        this.siteminderAuthService.logout();
        return;
      } catch (error) {
        this.logError('SiteMinder logout failed', error);
      }

      try {
        this.authSvc.signOut();
      } catch (error) {
        this.logError('Fallback sign out failed', error);
      }

      try {
        sessionStorage.clear();
      } catch (error) {
        this.logError('Session storage clear failed', error);
      }

      try {
        this.router.navigate(['/login']);
      } catch (error) {
        this.logError('Login redirect failed', error);
      }
    } catch (error) {
      this.logError('Session logout failed', error);

      try {
        this.router.navigate(['/login']);
      } catch (navigationError) {
        this.logError('Fallback login redirect failed', navigationError);
      }
    }
  }

  ngOnInit(): void {
    try {
      const url = this.router.url || '';

      if (url.startsWith('/logout')) {
        this.sessionlogOut();
        return;
      }
    } catch (error) {
      this.logError('Logout route check failed', error);
    }

    try {
      this.siteminderAuthService.initializeOAuthService();
    } catch (error) {
      this.logError('SiteMinder initialization failed', error);
    }

    try {
      this.logoutservice.currentMessage
        .pipe(takeUntil(this._destroyLogin))
        .subscribe({
          next: (res: any) => {
            this.isHeaderShow = res || false;
          },
          error: (error) => {
            this.logError('Logout status subscription failed', error);
          },
        });
    } catch (error) {
      this.logError('Logout status setup failed', error);
    }

    try {
      window.uniqueId = window.uniqueId || {};

      let counter = 0;

      window.uniqueId = () => {
        return 'myid-' + counter++;
      };
    } catch (error) {
      this.logError('Unique id setup failed', error);
    }

    try {
      this.menubarService.data
        .pipe(takeUntil(this._destroyLogin))
        .subscribe({
          next: (result) => {
            this.isExist = result;
          },
          error: (error) => {
            this.logError('Menu subscription failed', error);
          },
        });
    } catch (error) {
      this.logError('Menu setup failed', error);
    }
  }

  ngAfterViewInit(): void {
    try {
      this.menubarService.data
        .pipe(takeUntil(this._destroyLogin))
        .subscribe({
          next: (result) => {
            this.isExist = result;
          },
          error: (error) => {
            this.logError('Menu view subscription failed', error);
          },
        });
    } catch (error) {
      this.logError('Menu view setup failed', error);
    }
  }

  ngOnDestroy(): void {
    try {
      this.idleTimeoutService.resetTimer();
    } catch (error) {
      this.logError('Idle timer cleanup failed', error);
    }

    try {
      this._destroyLogin.next();
      this._destroyLogin.complete();
    } catch (error) {
      this.logError('Subscription cleanup failed', error);
    }

    try {
      this.errorMsgSub?.unsubscribe();
    } catch (error) {
      this.logError('Error subscription cleanup failed', error);
    }
  }

  private logError(context: string, error: unknown): void {
    try {
      const messages: string[] = [];
      let currentError: unknown = error;
      let depth = 0;

      while (currentError && depth < 5) {
        if (currentError instanceof Error) {
          messages.push(currentError.message || currentError.name);

          const nestedError = currentError as Error & {
            cause?: unknown;
            innerException?: unknown;
            innerError?: unknown;
          };

          currentError =
            nestedError.cause ??
            nestedError.innerException ??
            nestedError.innerError ??
            null;
        } else {
          messages.push('Unknown error');
          currentError = null;
        }

        depth++;
      }

      console.error(
        `${context}${messages.length ? `: ${messages.join(' | ')}` : ''}`
      );
    } catch {
      console.error(context);
    }
  }
}

declare global {
  interface Window {
    uniqueId: any;
  }
}